package com.trevorwiebe.timesheet.calendar.presentation

import androidx.compose.foundation.ExperimentalFoundationApi
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.grid.GridCells
import androidx.compose.foundation.lazy.grid.LazyVerticalGrid
import androidx.compose.foundation.lazy.grid.itemsIndexed
import androidx.compose.foundation.lazy.grid.rememberLazyGridState
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.lazy.rememberLazyListState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Text
import androidx.compose.material3.MaterialTheme
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableIntStateOf
import androidx.compose.runtime.remember
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import com.trevorwiebe.timesheet.calendar.presentation.composables.DayBlock
import com.trevorwiebe.timesheet.calendar.presentation.composables.DayList
import com.trevorwiebe.timesheet.calendar.presentation.composables.DayOfWeekText
import com.trevorwiebe.timesheet.calendar.presentation.composables.TimeOffReviewDialog
import com.trevorwiebe.timesheet.calendar.presentation.uiHelper.CalendarType
import com.trevorwiebe.timesheet.calendar.presentation.uiHelper.DayUi
import com.trevorwiebe.timesheet.core.domain.Util
import com.trevorwiebe.timesheet.core.presentation.TopBar
import com.trevorwiebe.timesheet.core.presentation.common.BackIcon
import com.trevorwiebe.timesheet.core.presentation.common.NativeDialog
import com.trevorwiebe.timesheet.core.presentation.common.ResponsiveContent
import com.trevorwiebe.timesheet.core.presentation.common.isSideNavVisible
import com.trevorwiebe.timesheet.theme.successGreenText
import kotlinx.datetime.Month
import kotlinx.datetime.isoDayNumber
import org.jetbrains.compose.resources.painterResource
import org.koin.compose.viewmodel.koinViewModel
import timesheet.composeapp.generated.resources.Res
import timesheet.composeapp.generated.resources.baseline_calendar_view_day_24
import timesheet.composeapp.generated.resources.baseline_calendar_view_month_24
import timesheet.composeapp.generated.resources.cancel_circle
import timesheet.composeapp.generated.resources.check_circle

@OptIn(ExperimentalFoundationApi::class)
@Composable
fun CalendarScreen(
    viewModel: CalendarViewModel = koinViewModel(),
    onBack: () -> Unit = {},
) {

    val state by viewModel.state.collectAsState()

    val lazyColumnState = rememberLazyListState()
    val lazyGridState = rememberLazyGridState()

    val defaultGridPosition = 1032
    val defaultListPosition = 1005

    val movableListPosition = remember { mutableIntStateOf(1000) }

    LaunchedEffect(Unit, state.calendarType) {
        if (state.calendarType == CalendarType.GRID) {
            lazyGridState.scrollToItem(defaultListPosition)
        } else {
            lazyColumnState.scrollToItem(defaultGridPosition)
        }
    }

    fun getIndexInListWithHeaders(
        targetIndex: Int,
        groupedDays: Map<Pair<Int, Month>, List<DayUi>>,
    ): Int {
        var currentIndex = 0
        var flatIndex = 0

        groupedDays.forEach { (_, daysInMonth) ->
            // Add one for the sticky header
            currentIndex++
            for (day in daysInMonth) {
                if (flatIndex == targetIndex) return currentIndex
                currentIndex++
                flatIndex++
            }
        }
        return defaultListPosition // fallback
    }

    val groupedDays = remember(state.calendarStructure) {
        state.calendarStructure.groupBy { it.date.year to it.date.month }
    }
    val targetListIndex = remember(movableListPosition.value, groupedDays) {
        getIndexInListWithHeaders(movableListPosition.value, groupedDays)
    }

    LaunchedEffect(targetListIndex, state.calendarType) {
        if (state.calendarType == CalendarType.LIST) {
            lazyColumnState.scrollToItem(targetListIndex - 1)
        }
    }

    Scaffold(
        topBar = {
            TopBar(
                title = if (state.timeOffMode) "Adding Time Off" else "Time Off",
                backIcon = if (isSideNavVisible()) null else ({ BackIcon() }),
                onBack = onBack,
                actions = {
                    if (state.timeOffMode) {
                        IconButton(
                            onClick = {
                                viewModel.onEvent(CalendarEvent.OnSetAddTimeOffMode(false, false))
                            }
                        ) {
                            Icon(
                                modifier = Modifier.size(48.dp),
                                painter = painterResource(Res.drawable.cancel_circle),
                                contentDescription = "cancel",
                                tint = MaterialTheme.colorScheme.error
                            )
                        }
                        Spacer(modifier = Modifier.width(20.dp))
                        IconButton(
                            onClick = {
                                viewModel.onEvent(CalendarEvent.OnSetAddTimeOffMode(false, true))
                            }
                        ) {
                            Icon(
                                modifier = Modifier.size(48.dp),
                                painter = painterResource(Res.drawable.check_circle),
                                contentDescription = "check",
                                tint = successGreenText
                            )
                        }
                        Spacer(modifier = Modifier.width(16.dp))
                    } else {
                        if (state.calendarType == CalendarType.GRID) {
                            IconButton(
                                onClick = {
                                    viewModel.onEvent(CalendarEvent.OnSetCalendarType(CalendarType.LIST))
                                }
                            ) {
                                Icon(
                                    painter = painterResource(Res.drawable.baseline_calendar_view_day_24),
                                    contentDescription = "calendar day view"
                                )
                            }
                        } else {
                            IconButton(
                                onClick = {
                                    viewModel.onEvent(CalendarEvent.OnSetCalendarType(CalendarType.GRID))
                                }
                            ) {
                                Icon(
                                    painter = painterResource(Res.drawable.baseline_calendar_view_month_24),
                                    contentDescription = "calendar month view"
                                )
                            }
                        }
                    }
                }
            )
        }
    ) { padding ->

        ResponsiveContent(modifier = Modifier.padding(padding)) {
        Column {

            if (state.calendarType == CalendarType.GRID) {

                Spacer(
                    modifier = Modifier
                        .fillMaxWidth()
                        .height(4.dp)
                )

                Row {
                    DayOfWeekText(dayInitial = "S", modifier = Modifier.weight(1f))
                    DayOfWeekText(dayInitial = "M", modifier = Modifier.weight(1f))
                    DayOfWeekText(dayInitial = "T", modifier = Modifier.weight(1f))
                    DayOfWeekText(dayInitial = "W", modifier = Modifier.weight(1f))
                    DayOfWeekText(dayInitial = "T", modifier = Modifier.weight(1f))
                    DayOfWeekText(dayInitial = "F", modifier = Modifier.weight(1f))
                    DayOfWeekText(dayInitial = "S", modifier = Modifier.weight(1f))
                }

                Spacer(
                    modifier = Modifier
                        .fillMaxWidth()
                        .height(4.dp)
                )

                val firstDate = state.calendarStructure.first().date.dayOfWeek.isoDayNumber

                LazyVerticalGrid(
                    state = lazyGridState,
                    columns = GridCells.Fixed(7)
                ) {
                    for (i in 1..firstDate) {
                        item {
                            Spacer(
                                modifier = Modifier
                                    .padding(2.dp)
                                    .clip(RoundedCornerShape(6.dp))
                                    .height(96.dp)
                            )
                        }
                    }
                    itemsIndexed(
                        items = state.calendarStructure
                    ) { index, dayUi ->
                        DayBlock(
                            dayUi = dayUi,
                            onClick = {
                                if (state.timeOffMode) {
                                    viewModel.onEvent(CalendarEvent.OnSetSelectedTimeOff(dayUi.date))
                                } else {
                                    movableListPosition.value = index
                                    viewModel.onEvent(CalendarEvent.OnSetCalendarType(CalendarType.LIST))
                                }
                            },
                            onLongClick = {
                                viewModel.onEvent(CalendarEvent.OnSetAddTimeOffMode(
                                    mode = true,
                                    saving = false
                                ))
                                viewModel.onEvent(CalendarEvent.OnSetSelectedTimeOff(dayUi.date))
                            }
                        )
                    }
                }
            } else {
                LazyColumn(
                    state = lazyColumnState
                ) {
                    state.calendarStructure
                        .groupBy { it.date.year to it.date.month }
                        .forEach { (yearMonth, daysInMonth) ->

                            stickyHeader {
                                // Replace with your own composable or styling
                                Text(
                                    text = "${yearMonth.second.name} ${yearMonth.first}",
                                    modifier = Modifier
                                        .fillMaxWidth()
                                        .background(MaterialTheme.colorScheme.background)
                                        .padding(
                                            start = 16.dp,
                                            top = 8.dp,
                                            bottom = 8.dp,
                                            end = 16.dp
                                        ),
                                    fontWeight = FontWeight.Bold,
                                    color = MaterialTheme.colorScheme.tertiary
                                )
                            }

                            items(daysInMonth) { dayUi ->
                                DayList(
                                    dayUi = dayUi,
                                    onClick = {
                                        if (state.timeOffMode) {
                                            viewModel.onEvent(
                                                CalendarEvent.OnSetSelectedTimeOff(
                                                    dayUi.date
                                                )
                                            )
                                        }
                                    },
                                    onLongClick = {
                                        viewModel.onEvent(
                                            CalendarEvent.OnSetAddTimeOffMode(
                                                mode = true,
                                                saving = false
                                            )
                                        )
                                        viewModel.onEvent(CalendarEvent.OnSetSelectedTimeOff(dayUi.date))
                                    },
                                    onEmployeeSelected = {
                                        if (state.isAdmin || it.request.employeeId == state.user?.id) {
                                            viewModel.onEvent(
                                                CalendarEvent.OnTimeOffSelected(it)
                                            )
                                        }
                                    }
                                )
                            }
                        }
                }
            }
        }
        }

        NativeDialog(
            confirmText = "Delete",
            onConfirm = {
                viewModel.onEvent(CalendarEvent.OnDeleteTimeOffRequest(state.timeOffModel))
                viewModel.onEvent(CalendarEvent.OnTimeOffSelected(null))
            },
            dismissText = "Cancel",
            onDismiss = {
                viewModel.onEvent(CalendarEvent.OnTimeOffSelected(null))
            },
            visible = state.showTimeOffDialog,
            title = "Delete Time Off",
            message = "Request date: ${Util.toFriendlyDate(state.timeOffModel?.request?.requestOffTime)}",
        )

        if (state.showReviewDialog) {
            TimeOffReviewDialog(
                timeOffRequest = state.timeOffModel,
                onApprove = {
                    viewModel.onEvent(CalendarEvent.OnReviewTimeOffRequest(state.timeOffModel, true))
                    viewModel.onEvent(CalendarEvent.OnTimeOffSelected(null))
                },
                onDeny = {
                    viewModel.onEvent(CalendarEvent.OnReviewTimeOffRequest(state.timeOffModel, false))
                    viewModel.onEvent(CalendarEvent.OnTimeOffSelected(null))
                },
                onDelete = {
                    viewModel.onEvent(CalendarEvent.OnDeleteTimeOffRequest(state.timeOffModel))
                    viewModel.onEvent(CalendarEvent.OnTimeOffSelected(null))
                },
                onDismiss = {
                    viewModel.onEvent(CalendarEvent.OnTimeOffSelected(null))
                },
            )
        }
    }
}